import React, { useState, useCallback } from 'react';
import { Record } from 'immutable';
import pick from 'lodash/pick';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';

// Hooks
import { useDeepEqualSelector } from 'Shared/utils/hooks';

// Utils
import { getSortedItems } from 'Shared/utils/sorting';
import { getIsDateAfter } from 'Shared/utils/DateTime.utils';

// Types
import { WorkspaceMeetingMode } from 'Shared/types/WorkspaceMeetingMode.type';

// Helpers
import WorkspaceMeetingModeHelper from 'Shared/model_helpers/WorkspaceMeetingMode.helper';

// Selectors
import { getItemsForStandardView } from 'Shared/selectors/workspace';

// Actions
import { addItem } from 'Shared/actions/todos';
import { updateSelectedUuids } from 'Shared/actions/selectedUuids';
import { createItemFromSourceItemAction } from 'Shared/actions/meetingItems';

// Components
import { WpContainer, WpContainerHeader, WpContainerBody } from 'Wpds/components/container';
import ActionItemsTable from 'Shared/components/ActionItems/ActionItemsTable/ActionItemsTable';
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';

// Styles
import './CreatedInMeetingMode.scss';

const ActionItemWhiteList = Object.freeze([
  'id',
  'uuid',
  'parentUuid',
  'workspacePath',
  'parentText',
  'isHighPriority',
  'workspaceId',
  'assignmentAvatar',
  'position',
]);

const sortingMap = {
  actionItems: 'default',
  owner: 'default',
  workspace: 'default',
  dueDate: 'default',
};

type Props = {
  meetingModeSession: Record<WorkspaceMeetingMode>;
};

const CreatedInMeetingMode = ({ meetingModeSession }: Props) => {
  const dispatch = useDispatch();
  const [selectedSort, setSelectedSort] = useState(sortingMap);

  const workspaceId = WorkspaceMeetingModeHelper.getWorkspaceSlug(meetingModeSession);
  const sessionCreatedAt = WorkspaceMeetingModeHelper.getCreatedAt(meetingModeSession);

  const actionItems = useDeepEqualSelector(state => [
    ...getSortedItems({
      items: getItemsForStandardView(state.workspace.meeting_items),
      selectedSort,
      withHighPriority: true,
      positionField: 'position',
    })
      .filter(actionItem => getIsDateAfter(sessionCreatedAt, actionItem.createdAt))
      .map(actionItem => pick(actionItem, ActionItemWhiteList)),
  ]);

  const handleCreateItem = useCallback(
    ({ sourceItemUuid, itemUuid }) => {
      const dueBy = moment(new Date()).startOf('day').startOf('hour').add(7, 'days').add(12, 'hours').unix();

      dispatch(
        createItemFromSourceItemAction({
          sourceItemUuid,
          itemUuid,
          isAssignCurrentUser: false,
          dueBy,
        })
      );
    },
    [dispatch]
  );

  const handleAddMeetingItem = useCallback(() => {
    const dueBy = moment(new Date()).startOf('day').startOf('hour').add(7, 'days').add(12, 'hours').unix();
    const itemUuid = uuidv4();

    dispatch(
      addItem({
        itemUuid,
        workspaceId,
        position: 1,
        dueBy,
        isAssignCurrentUser: true,
      })
    );
    dispatch(updateSelectedUuids(itemUuid, { isOneSelected: true }));
  }, [dispatch, workspaceId]);

  const hasActionItems = actionItems.length > 0;

  const baseClassName = 'CreatedInMeetingMode';
  return (
    <WpContainer className={baseClassName}>
      <WpContainerHeader>
        <div className={`${baseClassName}__header`}>
          Action Items Created During this meeting
          {hasActionItems && (
            <WpButton size="sm" variant="tertiary" onClick={handleAddMeetingItem}>
              <WpIcon name="plus-circle" />
              Add action item
            </WpButton>
          )}
        </div>
      </WpContainerHeader>
      <WpContainerBody>
        {hasActionItems && (
          <ActionItemsTable
            forbidDrag
            actionItems={actionItems}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
            withOwnerSection
            withWorkspaceSection={false}
            enableHideAnimation={false}
            onCreateItem={handleCreateItem}
          />
        )}
        {!hasActionItems && (
          <div className={`${baseClassName}__empty`}>
            <WpButton variant="tertiary" onClick={handleAddMeetingItem}>
              <WpIcon name="plus-circle" />
              Add action item
            </WpButton>
          </div>
        )}
      </WpContainerBody>
    </WpContainer>
  );
};

export default CreatedInMeetingMode;
